import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Award,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  AlertCircle,
  Check,
  ToggleLeft,
  ToggleRight,
  Loader,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  GET_GRADOS_MILITARES,
  CREAR_GRADO_MILITAR,
  ACTUALIZAR_GRADO_MILITAR,
  ELIMINAR_GRADO_MILITAR,
} from '../graphql/gradosMilitares';
import './GradosMilitares.css';

// ============================================================================
// Helpers
// ============================================================================

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

// La columna precio_noche almacena la TARIFA MENSUAL (30 noches).
// El backend la divide entre 30 al aplicarla por noche.
const DIAS_MES = 30;
const mensualAPorNoche = (mensual) => Number(mensual || 0) / DIAS_MES;

const FORM_INITIAL = {
  codigo: '',
  nombre: '',
  descripcion: '',
  precio_noche: '',           // tarifa mensual (30 noches)
  precio_noche_temporal: '',  // tarifa por noche
  orden: '',
  activo: true,
};

// ============================================================================
// Main component
// ============================================================================

const GradosMilitares = () => {
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [gradoEditando, setGradoEditando] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [form, setForm] = useState(FORM_INITIAL);

  // ---- Queries ----
  const { data, loading, refetch } = useQuery(GET_GRADOS_MILITARES, {
    variables: mostrarInactivos ? {} : { activo: true },
    fetchPolicy: 'cache-and-network',
  });

  // ---- Mutations ----
  const [crearGrado, { loading: creando }] = useMutation(CREAR_GRADO_MILITAR, {
    onCompleted: () => {
      toast.success('Grado militar creado correctamente');
      cerrarModal();
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const [actualizarGrado, { loading: actualizando }] = useMutation(ACTUALIZAR_GRADO_MILITAR, {
    onCompleted: () => {
      toast.success('Grado militar actualizado correctamente');
      cerrarModal();
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const [eliminarGrado, { loading: eliminando }] = useMutation(ELIMINAR_GRADO_MILITAR, {
    onCompleted: () => {
      toast.success('Grado militar eliminado');
      setConfirmEliminar(null);
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  // ---- Data ----
  const grados = [...(data?.gradosMilitares || [])].sort((a, b) => a.orden - b.orden);

  // ---- Handlers ----
  const abrirModalNuevo = () => {
    setGradoEditando(null);
    // Pre-fill orden with the next value
    const maxOrden = grados.reduce((max, g) => Math.max(max, g.orden || 0), 0);
    setForm({ ...FORM_INITIAL, orden: String(maxOrden + 1) });
    setModalAbierto(true);
  };

  const abrirModalEditar = (grado) => {
    setGradoEditando(grado);
    setForm({
      codigo: grado.codigo || '',
      nombre: grado.nombre || '',
      descripcion: grado.descripcion || '',
      precio_noche: String(grado.precio_noche || ''),
      precio_noche_temporal: String(grado.precio_noche_temporal || ''),
      orden: String(grado.orden || ''),
      activo: grado.activo,
    });
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setGradoEditando(null);
    setForm(FORM_INITIAL);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.codigo.trim()) {
      toast.error('Ingrese un codigo para el grado');
      return;
    }
    if (!form.nombre.trim()) {
      toast.error('Ingrese el nombre del grado');
      return;
    }
    if (!form.precio_noche || Number(form.precio_noche) < 0) {
      toast.error('Ingrese una tarifa mensual valida');
      return;
    }
    if (!form.orden || Number(form.orden) < 1) {
      toast.error('Ingrese un orden valido (numero positivo)');
      return;
    }

    const input = {
      codigo: form.codigo.trim().toUpperCase(),
      nombre: form.nombre.trim(),
      descripcion: form.descripcion.trim() || null,
      // Se guarda el valor mensual tal cual; el backend lo divide entre 30 al aplicarlo.
      precio_noche: parseFloat(form.precio_noche),
      precio_noche_temporal: parseFloat(form.precio_noche_temporal) || 0,
      orden: parseInt(form.orden),
      activo: form.activo,
    };

    if (gradoEditando) {
      actualizarGrado({ variables: { id: parseInt(gradoEditando.id), input } });
    } else {
      crearGrado({ variables: { input } });
    }
  };

  const handleToggleActivo = (grado) => {
    actualizarGrado({
      variables: {
        id: parseInt(grado.id),
        input: {
          codigo: grado.codigo,
          nombre: grado.nombre,
          descripcion: grado.descripcion || null,
          precio_noche: grado.precio_noche,
          orden: grado.orden,
          activo: !grado.activo,
        },
      },
    });
  };

  const handleEliminar = () => {
    if (!confirmEliminar) return;
    eliminarGrado({ variables: { id: parseInt(confirmEliminar.id) } });
  };

  // ---- Loading state ----
  if (loading && !data) {
    return (
      <div className="grados-militares">
        <div className="gm__loading">
          <Loader size={18} className="gm__spin" />
          Cargando grados militares...
        </div>
      </div>
    );
  }

  return (
    <div className="grados-militares">
      {/* Header */}
      <div className="gm__header">
        <div className="gm__title-group">
          <h1 className="gm__title">
            <Award size={22} />
            Grados Militares
          </h1>
          <p className="gm__subtitle">Clasificacion de rangos y precios por noche</p>
        </div>
        <button className="gm__btn gm__btn--primary" onClick={abrirModalNuevo}>
          <Plus size={14} />
          Agregar Grado
        </button>
      </div>

      {/* Filters row */}
      <div className="gm__filters">
        <label className="gm__toggle">
          <input
            type="checkbox"
            checked={mostrarInactivos}
            onChange={(e) => setMostrarInactivos(e.target.checked)}
          />
          <span className="gm__toggle-track" />
          <span className="gm__toggle-label">Mostrar inactivos</span>
        </label>

        <span className="gm__count">
          {grados.length} grado{grados.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="gm__table-container">
        {grados.length === 0 ? (
          <div className="gm__empty">
            <AlertCircle size={28} />
            <p className="gm__empty-title">No hay grados militares configurados</p>
            <p className="gm__empty-desc">
              Configure los grados militares con sus precios por noche para que
              puedan ser asignados a los huespedes durante el proceso de hospedaje.
            </p>
            <button className="gm__btn gm__btn--primary" onClick={abrirModalNuevo}>
              <Plus size={14} />
              Agregar primer grado
            </button>
          </div>
        ) : (
          <table className="gm__table">
            <thead>
              <tr>
                <th>Orden</th>
                <th>Codigo</th>
                <th>Nombre</th>
                <th>Descripcion</th>
                <th>Mensual (30 dias)</th>
                <th>Temporal / Noche</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {grados.map((grado) => (
                <tr key={grado.id} className={!grado.activo ? 'gm__row--inactivo' : ''}>
                  <td data-label="Orden">
                    <span className="gm__orden-badge">{grado.orden}</span>
                  </td>
                  <td data-label="Codigo">
                    <span className="gm__codigo">{grado.codigo}</span>
                  </td>
                  <td data-label="Nombre">
                    <span className="gm__nombre">{grado.nombre}</span>
                  </td>
                  <td data-label="Descripcion">
                    {grado.descripcion ? (
                      <span className="gm__descripcion" title={grado.descripcion}>
                        {grado.descripcion.length > 50
                          ? grado.descripcion.slice(0, 50) + '...'
                          : grado.descripcion}
                      </span>
                    ) : (
                      <span className="gm__empty-cell">—</span>
                    )}
                  </td>
                  <td data-label="Mensual (30 dias)">
                    <span className="gm__precio">{formatCurrency(grado.precio_noche)}</span>
                    <span className="gm__field-hint" style={{ display: 'block', marginTop: 2 }}>
                      {formatCurrency(mensualAPorNoche(grado.precio_noche))} / noche
                    </span>
                  </td>
                  <td data-label="Temporal / Noche">
                    <span className="gm__precio">{formatCurrency(grado.precio_noche_temporal)}</span>
                  </td>
                  <td data-label="Estado">
                    <button
                      className={`gm__badge ${grado.activo ? 'gm__badge--activo' : 'gm__badge--inactivo'}`}
                      onClick={() => handleToggleActivo(grado)}
                      disabled={actualizando}
                      title={grado.activo ? 'Desactivar grado' : 'Activar grado'}
                      aria-label={`${grado.activo ? 'Desactivar' : 'Activar'} grado ${grado.nombre}`}
                    >
                      {grado.activo ? (
                        <>
                          <Check size={11} />
                          Activo
                        </>
                      ) : (
                        <>
                          <X size={11} />
                          Inactivo
                        </>
                      )}
                    </button>
                  </td>
                  <td data-label="Acciones">
                    <div className="gm__actions-cell">
                      <button
                        className="gm__action-btn gm__action-btn--edit"
                        onClick={() => abrirModalEditar(grado)}
                        title="Editar grado"
                        aria-label={`Editar grado ${grado.nombre}`}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="gm__action-btn gm__action-btn--delete"
                        onClick={() => setConfirmEliminar(grado)}
                        title="Eliminar grado"
                        aria-label={`Eliminar grado ${grado.nombre}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit Modal */}
      {modalAbierto && (
        <GradoModal
          form={form}
          setForm={setForm}
          editando={gradoEditando}
          guardando={creando || actualizando}
          onSubmit={handleSubmit}
          onClose={cerrarModal}
        />
      )}

      {/* Delete confirmation modal */}
      {confirmEliminar && (
        <div className="gm__modal-overlay" onClick={() => setConfirmEliminar(null)}>
          <div className="gm__modal gm__modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="gm__modal-header">
              <span className="gm__modal-title">Confirmar eliminacion</span>
              <button
                className="gm__modal-close"
                onClick={() => setConfirmEliminar(null)}
                aria-label="Cerrar"
              >
                <X size={18} />
              </button>
            </div>
            <div className="gm__modal-body">
              <div className="gm__confirm-icon">
                <Trash2 size={24} />
              </div>
              <p className="gm__confirm-text">
                Esta a punto de eliminar el grado{' '}
                <strong>"{confirmEliminar.nombre}"</strong>.
                Esta accion no se puede deshacer.
              </p>
            </div>
            <div className="gm__modal-footer">
              <button
                className="gm__btn"
                onClick={() => setConfirmEliminar(null)}
                disabled={eliminando}
              >
                Cancelar
              </button>
              <button
                className="gm__btn gm__btn--danger"
                onClick={handleEliminar}
                disabled={eliminando}
              >
                {eliminando ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// GradoModal — create / edit
// ============================================================================

const GradoModal = ({ form, setForm, editando, guardando, onSubmit, onClose }) => {
  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  return (
    <div className="gm__modal-overlay" onClick={onClose}>
      <div className="gm__modal" onClick={(e) => e.stopPropagation()}>
        <div className="gm__modal-header">
          <span className="gm__modal-title">
            <Award size={16} />
            {editando ? 'Editar Grado Militar' : 'Nuevo Grado Militar'}
          </span>
          <button className="gm__modal-close" onClick={onClose} aria-label="Cerrar modal">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit}>
          <div className="gm__modal-body">
            {/* Codigo + Orden row */}
            <div className="gm__field-row">
              <div className="gm__field">
                <label htmlFor="gm-codigo">Codigo *</label>
                <input
                  id="gm-codigo"
                  type="text"
                  placeholder="Ej: CG, GEN, COL..."
                  value={form.codigo}
                  onChange={set('codigo')}
                  maxLength={20}
                  style={{ textTransform: 'uppercase' }}
                  required
                />
              </div>
              <div className="gm__field">
                <label htmlFor="gm-orden">Orden *</label>
                <input
                  id="gm-orden"
                  type="number"
                  placeholder="1"
                  min="1"
                  max="999"
                  value={form.orden}
                  onChange={set('orden')}
                  required
                />
              </div>
            </div>

            {/* Nombre */}
            <div className="gm__field">
              <label htmlFor="gm-nombre">Nombre del Grado *</label>
              <input
                id="gm-nombre"
                type="text"
                placeholder="Ej: General, Coronel, Capitan..."
                value={form.nombre}
                onChange={set('nombre')}
                maxLength={100}
                required
              />
            </div>

            {/* Descripcion */}
            <div className="gm__field">
              <label htmlFor="gm-descripcion">Descripcion</label>
              <textarea
                id="gm-descripcion"
                placeholder="Descripcion o notas adicionales del grado..."
                value={form.descripcion}
                onChange={set('descripcion')}
                maxLength={500}
              />
            </div>

            {/* Precios */}
            <div className="gm__field-row">
              <div className="gm__field">
                <label htmlFor="gm-precio">Tarifa Mensual - 30 dias (COP) *</label>
                <input
                  id="gm-precio"
                  type="number"
                  placeholder="0"
                  min="0"
                  step="any"
                  value={form.precio_noche}
                  onChange={set('precio_noche')}
                  required
                />
                {form.precio_noche && Number(form.precio_noche) > 0 && (
                  <span className="gm__field-hint">
                    Equivale a {formatCurrency(mensualAPorNoche(form.precio_noche))} / noche
                  </span>
                )}
              </div>
              <div className="gm__field">
                <label htmlFor="gm-precio-temporal">Tarifa Temporal por Noche (COP)</label>
                <input
                  id="gm-precio-temporal"
                  type="number"
                  placeholder="0"
                  min="0"
                  step="any"
                  value={form.precio_noche_temporal}
                  onChange={set('precio_noche_temporal')}
                />
                {form.precio_noche_temporal && Number(form.precio_noche_temporal) > 0 && (
                  <span className="gm__field-hint">
                    {formatCurrency(form.precio_noche_temporal)} / noche
                  </span>
                )}
              </div>
            </div>

            {/* Activo toggle */}
            <div className="gm__field gm__field--inline">
              <label className="gm__inline-toggle" htmlFor="gm-activo">
                <input
                  id="gm-activo"
                  type="checkbox"
                  checked={form.activo}
                  onChange={set('activo')}
                />
                <span className="gm__toggle-track" />
                <span className="gm__inline-label">
                  {form.activo ? (
                    <>
                      <ToggleRight size={15} style={{ color: 'var(--hotel-primary)' }} />
                      Grado activo
                    </>
                  ) : (
                    <>
                      <ToggleLeft size={15} />
                      Grado inactivo
                    </>
                  )}
                </span>
              </label>
            </div>
          </div>

          <div className="gm__modal-footer">
            <button type="button" className="gm__btn" onClick={onClose} disabled={guardando}>
              Cancelar
            </button>
            <button type="submit" className="gm__btn gm__btn--primary" disabled={guardando}>
              {guardando ? (
                <>
                  <Loader size={13} className="gm__spin" />
                  {editando ? 'Guardando...' : 'Creando...'}
                </>
              ) : (
                <>
                  <Save size={13} />
                  {editando ? 'Guardar Cambios' : 'Crear Grado'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GradosMilitares;
